import * as Sentry from "@sentry/react";
import { useCallback, useSyncExternalStore, type RefObject } from "react";

function subscribeToFullscreenChange(onStoreChange: () => void) {
  document.addEventListener("fullscreenchange", onStoreChange);
  return () => document.removeEventListener("fullscreenchange", onStoreChange);
}

/** The element in native fullscreen, which covers the rest of the page. */
function useFullscreenElement() {
  return useSyncExternalStore(
    subscribeToFullscreenChange,
    () => document.fullscreenElement,
    () => null,
  );
}

export function useHasFullscreenElement() {
  return useFullscreenElement() !== null;
}

export function useFullscreen(ref: RefObject<HTMLElement | null>) {
  const fullscreenElement = useFullscreenElement();
  const isFullscreen =
    fullscreenElement !== null && fullscreenElement === ref.current;

  const toggle = useCallback(async () => {
    const element = ref.current;
    if (!element) {
      return;
    }
    const isExiting = document.fullscreenElement === element;
    try {
      if (isExiting) {
        await document.exitFullscreen();
      } else {
        await element.requestFullscreen();
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { fullscreenAction: isExiting ? "exit" : "enter" },
      });
    }
  }, [ref]);

  return { isFullscreen, toggle };
}
